import { useEffect, useState } from 'react';
import { installHint, isIos, isStandalone } from '../registerServiceWorker';
import { отпустить } from '../sentry';

const DISMISS_KEY = 'mathpaw.install-dismissed';

function readDismissed(): boolean {
  try {
    return window.localStorage.getItem(DISMISS_KEY) === 'да';
  } catch {
    return false;
  }
}

function writeDismissed(): void {
  try {
    window.localStorage.setItem(DISMISS_KEY, 'да');
  } catch {
    /* приватное окно: предложение вернётся при следующем запуске */
  }
}

/** Событие Chromium, дающее кнопку установки. На iOS его нет вовсе. */
interface InstallEvent extends Event {
  prompt: () => Promise<void>;
}

/** Предложение поставить приложение на устройство.
 *
 *  Это не украшение и не «сделайте нас удобнее». На iOS без установки
 *  Safari стирает хранилище после семи дней без захода — то есть занятия
 *  ребёнка исчезают с устройства сами. Облако их вернёт, но только после
 *  входа и при сети, а ребёнок в дороге увидит пустое место.
 *
 *  Показывается на главном экране, до первой тренировки (спека 4.5), и
 *  убирается навсегда одним нажатием: предложение, которое нельзя закрыть,
 *  — это баннер, а баннеры перестают читать. */
export function InstallHint() {
  const [event, setEvent] = useState<InstallEvent | null>(null);
  const [dismissed, setDismissed] = useState(readDismissed);

  useEffect(() => {
    const onPrompt = (raw: Event) => {
      raw.preventDefault();
      setEvent(raw as InstallEvent);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  const hint = installHint({
    standalone: isStandalone(),
    ios: isIos(),
    promptAvailable: event !== null,
    dismissed,
  });
  if (hint === 'none') return null;

  const close = () => {
    writeDismissed();
    setDismissed(true);
  };

  return (
    <section className="flex flex-col gap-2 rounded-xl border border-line bg-accent-soft p-3">
      <p className="m-0 text-[0.95rem] text-ink">
        {hint === 'ios'
          ? 'Добавьте тренажёр на домашний экран: кнопка «Поделиться» внизу браузера → «На экран „Домой“». Так занятия не пропадут, даже если не заходить неделю.'
          : 'Тренажёр можно поставить на устройство — он будет открываться как приложение и работать без интернета.'}
      </p>
      <div className="flex flex-wrap gap-2">
        {hint === 'prompt' && (
          <button
            type="button"
            className="min-h-11 rounded-lg border border-line bg-card px-3 py-2 text-sm text-ink"
            onClick={() => {
              /** Родное диалоговое окно браузера — своя, чужая поверхность:
               *  отказ `prompt()` не портит наш баннер, `close()` убирает
               *  его в любом случае. Отказ всё же назван, а не отдан
               *  безымянному глобальному перехватчику необработанных
               *  обещаний. */
              отпустить(event?.prompt(), 'InstallHint:prompt');
              close();
            }}
          >
            Установить
          </button>
        )}
        <button
          type="button"
          className="min-h-11 rounded-lg px-3 py-2 text-sm text-muted underline"
          onClick={close}
        >
          Не надо
        </button>
      </div>
    </section>
  );
}
